import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';

const LOGO_ASPECT = 3 / 1; // 300x100 ratio — standard website header logo
const LOGO_OUTPUT_WIDTH = 600; // 2x for retina
const LOGO_OUTPUT_HEIGHT = 200;

interface LogoUploaderProps {
  currentUrl: string;
  onSave: (dataUrl: string) => void;
}

export function LogoUploader({ currentUrl, onSave }: LogoUploaderProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsSaving(true);

    try {
      const croppedDataUrl = await cropImage(imageSrc, croppedAreaPixels);
      onSave(croppedDataUrl);
      setImageSrc(null);
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-2">Logo</label>

      {/* Current logo preview + upload trigger */}
      <div className="mb-2" style={{ maxWidth: 'calc(33.333% - 4px)' }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className={`relative flex items-center justify-center rounded-lg border-2 border-dashed transition-all hover:border-primary-400 hover:bg-primary-50 w-full aspect-[8/3] ${
            currentUrl ? 'border-gray-200 bg-white' : 'border-gray-300 bg-gray-50'
          }`}
        >
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="Logo"
              className="w-full h-full object-contain rounded-lg p-1"
            />
          ) : (
            <div className="text-center">
              <svg className="w-5 h-5 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-[10px] text-gray-400 mt-0.5 block">Upload logo</span>
            </div>
          )}
          {currentUrl && (
            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-lg transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="text-xs text-gray-700 bg-white/90 px-2 py-1 rounded font-medium">Change</span>
            </div>
          )}
        </button>
        <p className="text-[10px] text-gray-400 mt-1">Recommended: 600x200px (3:1) — PNG or JPG, transparent bg works best</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Crop modal */}
      {imageSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setImageSrc(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            {/* Crop area */}
            <div className="relative w-full" style={{ height: 300 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={LOGO_ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={false}
                style={{
                  containerStyle: { background: '#f9fafb' },
                  cropAreaStyle: { border: '2px solid #4f46e5' },
                }}
              />
            </div>

            {/* Zoom slider */}
            <div className="px-6 py-3 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  className="flex-1 accent-primary-600"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setImageSrc(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving ? 'Cropping...' : 'Save Logo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Crop an image to the specified area and return a data URL.
 * Outputs at LOGO_OUTPUT_WIDTH x LOGO_OUTPUT_HEIGHT for retina quality.
 */
async function cropImage(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = LOGO_OUTPUT_WIDTH;
  canvas.height = LOGO_OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    LOGO_OUTPUT_WIDTH,
    LOGO_OUTPUT_HEIGHT,
  );

  return canvas.toDataURL('image/png', 0.9);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
